export type TelegramOS = 'ios' | 'android' | 'other'

export interface TelegramInfo {
  isTelegram: boolean
  os: TelegramOS
}

/**
 * Telegram in-app browser userAgent tokens.
 *
 * Recent Telegram Android appends `Telegram-Android/<version>` to the UA and
 * some builds carry a bare `Telegram` / `TgWebView` token. iOS Telegram does
 * NOT expose any UA token (its in-app browser mimics Safari), so the UA regex
 * alone cannot catch it — the runtime bridge check below covers that case.
 */
const TELEGRAM_UA_RE = /Telegram-(Android|iOS)|TgWebView|\bTelegram\b/i

const detectOS = (ua: string): TelegramOS => {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'other'
}

/**
 * Runtime properties injected by the Telegram in-app browser JS bridge.
 * This is the only reliable signal for Telegram iOS and for older Telegram
 * Android builds that ship a plain Chrome userAgent.
 */
const hasTelegramBridge = (w?: Window & typeof globalThis): boolean => {
  if (!w) return false
  const anyW = w as any
  return Boolean(
    anyW.TelegramWebview || anyW.TelegramWebviewProxy || anyW.TelegramWebviewProxyProto,
  )
}

/**
 * Detect whether the page is rendered inside the Telegram in-app browser
 * (the built-in browser opened from a link inside a Telegram chat — not a
 * Mini App). Google/Facebook OAuth is blocked there with `disallowed_useragent`,
 * so we need to route the user to an external browser.
 *
 * Detection is intentionally Telegram-only: it never matches regular browsers,
 * which keeps Google auth untouched everywhere else.
 */
export const detectTelegram = (
  userAgent?: string,
  win?: Window & typeof globalThis,
): TelegramInfo => {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const w = win ?? (typeof window !== 'undefined' ? window : undefined)

  if (!ua) return { isTelegram: false, os: 'other' }

  const isTelegram = TELEGRAM_UA_RE.test(ua) || hasTelegramBridge(w)

  return { isTelegram, os: detectOS(ua) }
}

/** Android intent URL that opens the current page in the default browser. */
export const buildAndroidIntentUrl = (url: string): string => {
  const stripped = url.replace(/^https?:\/\//, '')
  const fallback = encodeURIComponent(url)
  return `intent://${stripped}#Intent;scheme=https;S.browser_fallback_url=${fallback};end;`
}

/** iOS scheme that hands the current page off to Safari. */
export const buildIosSafariUrl = (url: string): string => `x-safari-${url}`
